import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AudioLines, Mic, ScanLine, Volume2, Wifi, BatteryFull } from "lucide-react";

const MODES = [
  {
    id: "scan",
    chip: "Scanning document",
    bubble: "Reading aloud: “Your appointment is confirmed for Tuesday, June 10th at 2:30 PM…”",
  },
  {
    id: "voice",
    chip: "Listening…",
    bubble: "“VoiceLens, what does this say?” — Reading the label in a clear, natural voice.",
  },
  {
    id: "explain",
    chip: "AI Explain Mode",
    bubble: "Plain English: “This bill says you owe $42.16 by July 1st. No late fees yet.”",
  },
];

const TEXT_LINES = [92, 100, 84, 96, 68, 90, 76];

const BADGES = [
  { label: "Voice Recognition", className: "-left-6 top-6 sm:-left-14" },
  { label: "Camera Scanning", className: "-right-4 top-32 sm:-right-10" },
  { label: "AI Explanation", className: "-left-6 bottom-44 sm:-left-14" },
  { label: "Large Accessible UI", className: "-right-4 bottom-8 sm:-right-10" },
];

export default function PhoneMockup() {
  const [modeIndex, setModeIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setModeIndex((i) => (i + 1) % MODES.length), 4200);
    return () => clearInterval(id);
  }, []);

  const mode = MODES[modeIndex];

  return (
    <div className="relative" data-testid="phone-mockup" role="img" aria-label="Animated smartphone mockup showing VoiceLens AI scanning a document, recognizing voice commands, and explaining text">
      <div
        className="absolute inset-0 -z-10 scale-110 rounded-full bg-[radial-gradient(circle_at_center,rgba(0,212,255,0.22),transparent_65%)] blur-2xl"
        aria-hidden="true"
      />

      {BADGES.map((b, i) => (
        <div
          key={b.label}
          aria-hidden="true"
          className={`glass-panel animate-float absolute z-20 hidden rounded-full px-4 py-2 text-xs font-semibold text-[#00D4FF] shadow-[0_0_18px_rgba(0,212,255,0.2)] md:block ${b.className}`}
          style={{ animationDelay: `${i * 1.2}s` }}
        >
          {b.label}
        </div>
      ))}

      <div className="relative w-[290px] rounded-[2.6rem] border border-[#00D4FF]/25 bg-[#0A1E3C] p-2.5 shadow-[0_30px_80px_rgba(0,0,0,0.55),0_0_50px_rgba(0,212,255,0.15)] sm:w-[320px]">
        <div className="relative overflow-hidden rounded-[2.1rem] bg-[#050F1F]">
          <div className="flex items-center justify-between px-6 pt-4 text-[10px] font-semibold text-slate-300" aria-hidden="true">
            <span>9:41</span>
            <span className="h-5 w-20 rounded-full bg-black/80" />
            <span className="flex items-center gap-1.5">
              <Wifi className="h-3 w-3" />
              <BatteryFull className="h-3.5 w-3.5" />
            </span>
          </div>

          <div className="flex items-center justify-between px-5 pt-4" aria-hidden="true">
            <span className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-[#2563EB] to-[#00D4FF]">
                <AudioLines className="h-3.5 w-3.5 text-white" />
              </span>
              <span className="font-display text-xs font-bold text-white">VoiceLens AI</span>
            </span>
            <span className="flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
              Live
            </span>
          </div>

          <div className="relative mx-5 mt-4 h-44 overflow-hidden rounded-2xl border border-[#00D4FF]/20 bg-[#0C2340]" aria-hidden="true">
            <div className="absolute left-3 top-3 h-5 w-5 rounded-tl-lg border-l-2 border-t-2 border-[#00D4FF]" />
            <div className="absolute right-3 top-3 h-5 w-5 rounded-tr-lg border-r-2 border-t-2 border-[#00D4FF]" />
            <div className="absolute bottom-3 left-3 h-5 w-5 rounded-bl-lg border-b-2 border-l-2 border-[#00D4FF]" />
            <div className="absolute bottom-3 right-3 h-5 w-5 rounded-br-lg border-b-2 border-r-2 border-[#00D4FF]" />
            <div className="space-y-2.5 px-7 pt-7">
              {TEXT_LINES.map((w, i) => (
                <div
                  key={i}
                  className="h-2 rounded-full bg-slate-400/25"
                  style={{ width: `${w}%` }}
                />
              ))}
            </div>
            <div className="scan-beam-line absolute left-3 right-3 h-[3px] rounded-full bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent shadow-[0_0_16px_#00D4FF]" />
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-[9px] font-semibold text-[#00D4FF] backdrop-blur">
              <ScanLine className="h-3 w-3" />
              OCR Active
            </div>
          </div>

          <div className="flex items-end justify-center gap-1 px-5 pt-4" aria-hidden="true">
            {Array.from({ length: 21 }).map((_, i) => (
              <span
                key={i}
                className="wave-bar w-1 rounded-full bg-gradient-to-t from-[#2563EB] to-[#00D4FF]"
                style={{ height: `${10 + Math.abs(10 - i) * 1.8}px`, animationDelay: `${i * 0.07}s` }}
              />
            ))}
          </div>

          <div className="px-5 pt-4" aria-live="off">
            <AnimatePresence mode="wait">
              <motion.div
                key={mode.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
              >
                <span className="mb-2 inline-block rounded-full bg-[#2563EB]/25 px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-[#00D4FF]">
                  {mode.chip}
                </span>
                <div className="min-h-[64px] rounded-2xl rounded-bl-md border border-[#00D4FF]/15 bg-[#0C2340]/90 p-3 text-[11px] leading-relaxed text-slate-200">
                  {mode.bubble}
                  <span className="blink-caret ml-0.5 inline-block h-3 w-[2px] translate-y-0.5 bg-[#00D4FF]" />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-center gap-4 px-5 py-5" aria-hidden="true">
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#00D4FF]/25 text-[#00D4FF]">
              <Volume2 className="h-4 w-4" />
            </span>
            <span className="mic-pulse flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#2563EB] to-[#00D4FF]">
              <Mic className="h-6 w-6 text-white" />
            </span>
            <span className="max-w-[80px] text-left text-[9px] font-semibold leading-tight text-slate-400">
              Say “Read this”
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
